import type { i18n } from 'i18next';
import { setLocale } from 'yup';

// Callbacks resolve messages when validation runs, including after a language change.
export function configureValidationTranslations(translator: i18n) {
  const message = (key: string, values?: Record<string, unknown>) => () =>
    translator.t(key, values);

  setLocale({
    mixed: {
      default: message('validation.invalid'),
      required: message('formValidation.fieldRequired'),
      defined: message('formValidation.fieldRequired'),
      oneOf: message('validation.oneOf'),
      notOneOf: message('validation.notOneOf'),
      notType: ({ type }) => {
        if (type === 'number')
          return translator.t('formValidation.validNumber');
        if (type === 'date') return translator.t('formValidation.validDate');
        if (type === 'object') return translator.t('validation.selectOption');
        if (type === 'array') return translator.t('validation.validList');
        if (type === 'boolean') return translator.t('validation.validBoolean');
        return translator.t('validation.invalid');
      }
    },
    string: {
      length: ({ length }) =>
        translator.t('validation.stringLength', { length }),
      min: ({ min }) => translator.t('validation.stringMin', { min }),
      max: ({ max }) => translator.t('validation.stringMax', { max }),
      matches: message('validation.invalidFormat'),
      email: message('formValidation.validEmail'),
      url: message('formValidation.validUrl'),
      uuid: message('validation.validIdentifier'),
      trim: message('validation.trim'),
      lowercase: message('validation.lowercase'),
      uppercase: message('validation.uppercase')
    },
    number: {
      min: ({ min }) => translator.t('validation.numberMin', { min }),
      max: ({ max }) => translator.t('validation.numberMax', { max }),
      lessThan: ({ less }) =>
        translator.t('validation.numberLessThan', { less }),
      moreThan: ({ more }) =>
        translator.t('validation.numberMoreThan', { more }),
      positive: message('formValidation.positiveNumber'),
      negative: message('validation.negativeNumber'),
      integer: message('formValidation.validInteger')
    },
    date: {
      min: message('validation.dateMin'),
      max: message('validation.dateMax')
    },
    object: { noUnknown: message('validation.unknownFields') },
    array: {
      length: ({ length }) =>
        translator.t('validation.arrayLength', { length }),
      min: ({ min }) => translator.t('validation.arrayMin', { min }),
      max: ({ max }) => translator.t('validation.arrayMax', { max })
    },
    boolean: { isValue: message('validation.validBoolean') }
  });
}
